import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { map, catchError, switchMap, take } from 'rxjs/operators';

import { common } from '@qwyk/models';

import { CustomTagsActions } from './custom-tags.actions';
import { CustomTagsService } from '../custom-tags-service';
import { loadShipments, tagShipment, untagShipment } from '../../../../shipments/src/lib/+state/shipments.actions';

@Injectable()
export class CustomTagsEffects {

    private store = inject(Store);
    private actions$ = inject(Actions);
    private toastrService = inject(ToastrService);
    private translate = inject(TranslateService);
    private service = inject(CustomTagsService);

    createTag$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomTagsActions.tagEntity),
            switchMap(action => {
                return this.service
                    .tagEntity({ data: action.data })
                    .pipe(
                        map(response =>
                            CustomTagsActions.tagEntitySuccess(response)
                        ),
                        catchError(error => {
                            if (error.status === 500) {
                                return of(
                                    CustomTagsActions.backendFailure({ error })
                                );
                            }

                            return of(
                                CustomTagsActions.tagEntityFailure({
                                    error,
                                })
                            );
                        })
                    )
            })
        )
    );

    linkTagToEntity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomTagsActions.linkTagToEntity),
            switchMap(action => {
                const firstTagInAction = action.data && action.data.length > 0 ? action.data[0] : undefined;

                if (!firstTagInAction || !firstTagInAction.attributes || !firstTagInAction.attributes.type_id || firstTagInAction.attributes.type_id.length === 0) {
                    return of(CustomTagsActions.linkTagToEntityFailure({ error: 'Error' }));
                }
                const entityId = firstTagInAction.attributes.type_id[0];
                const type = firstTagInAction.attributes.type;
                const tagId = action.tagId;

                if (!type || !tagId) {
                    return of(CustomTagsActions.linkTagToEntityFailure({ error: 'Error' }));
                }

                return this.service
                    .linkTagToEntity({ entityId, tagId, type })
                    .pipe(
                        map(response => {
                            const tag = response.find(t => t.id === tagId);

                            if (!tag) {
                                return CustomTagsActions.linkTagToEntityFailure({
                                    error: `Error`,
                                });
                            }
                            return CustomTagsActions.linkTagToEntitySuccess({ tag, entityId });
                        }),
                        catchError(error => {
                            if (error.status === 500) {
                                return of(
                                    CustomTagsActions.backendFailure({ error })
                                );
                            }

                            return of(
                                CustomTagsActions.linkTagToEntityFailure({
                                    error,
                                })
                            )
                        })
                    )
            })
        )
    );

    unlinkTagFromEntity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomTagsActions.unlinkTagFromEntity),
            switchMap(action => {
                const { tagId, entityId, entity } = action;

                return this.service
                    .unlinkTagFromEntity({ tagId, entityId, type: entity })
                    .pipe(
                        map(data => {
                            return CustomTagsActions.unlinkTagFromEntitySuccess(
                                { tagId, entityId }
                            )
                        }),
                        catchError(error => {
                            if (error.status === 500) {
                                return of(
                                    CustomTagsActions.backendFailure({ error })
                                );
                            }

                            return of(
                                CustomTagsActions.unlinkTagFromEntityFailure({
                                    error,
                                })
                            );
                        })
                    );
            })
        )
    );

    refreshShipmentsAfterCreatingTags$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomTagsActions.tagEntitySuccess),
            tap((action: { data?: (common.CustomTag | undefined)[] }) => {
                const sourceActionData = action.data;

                if (!sourceActionData || sourceActionData.length === 0) {
                    console.error('tagEntitySuccess was dispatched with no tag data. Cannot refresh shipment.');
                    return;
                }

                const definedTags = sourceActionData.filter((tag): tag is common.CustomTag => !!tag);

                if (definedTags.length === 0) {
                    console.error('tagEntitySuccess contained no defined tags after filtering. Cannot refresh shipment.');
                    return;
                }

                const createdTag = definedTags[0];

                if (!createdTag.attributes ||
                    !createdTag.attributes.type_id ||
                    createdTag.attributes.type_id.length === 0 ||
                    !createdTag.attributes.type_id[0]
                ) {
                    console.error(
                        'Invalid tag structure or missing type_id in tagEntitySuccess payload. Cannot refresh shipment.',
                        createdTag
                    );
                    return;
                }

                const entityId = createdTag.attributes.type_id[0];
                this.store.dispatch(tagShipment({ entityId, tag: createdTag }));
            })
        ),
        { dispatch: false }
    );

    refreshShipmentsAfterUpdatingTags$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                CustomTagsActions.updateTagSuccess
            ),
            tap(() => {
                this.store.dispatch(loadShipments({ query: null }));
            })
        ),
        { dispatch: false }
    );

    refreshShipmentsAfterLinkingTags$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                CustomTagsActions.linkTagToEntitySuccess
            ),
            tap((res) => {
                const { entityId, tag } = res;
                this.store.dispatch(tagShipment({ entityId, tag }));
            })
        ),
        { dispatch: false }
    );

    refreshShipmentsAfterUnlinkingTags$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                CustomTagsActions.unlinkTagFromEntitySuccess
            ),
            tap((res) => {
                const { entityId, tagId } = res;
                this.store.dispatch(untagShipment({ entityId, tagId }));
            })
        ),
        { dispatch: false }
    );

    searchCustomTags$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomTagsActions.searchTags),
            switchMap(action => {
                return this.service.getCustomTagsByName(action.name).pipe(
                    take(1),
                    map((response: common.SearchCustomTag[]) => {
                        const responseWithDefaultValue = [
                            ...response,
                            {
                                id: '0',
                                name: 'Add new tag',
                                relevance: 0,
                                query: action.name
                            }];
                        return CustomTagsActions.searchTagsSuccess({
                            custom_tags: responseWithDefaultValue,
                        })
                    }),
                    catchError((error) => {
                        console.error(error);
                        return of(CustomTagsActions.searchTagsFailure({ error }));
                    })
                );
            })
        )
    );

    loadCustomTags$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomTagsActions.loadTags),
            switchMap(action => {
                return this.service
                    .getCustomTagsPaginated({ ...action.query })
                    .pipe(
                        take(1),
                        map(response =>
                            CustomTagsActions.loadTagsSuccess({
                                custom_tags: response.data,
                                pagination: response.meta,
                                isSuggestion: action.isSuggestion
                            })
                        ),
                        catchError(error => {
                            console.error(error);
                            if (error.status === 500) {
                                return of(
                                    CustomTagsActions.backendFailure({ error })
                                );
                            }
                            return of(CustomTagsActions.loadTagsFailure({ error }));
                        })
                    );
            })
        )
    );

    updateCustomTag$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CustomTagsActions.updateTag),
            switchMap(action => {
                return this.service
                    .updateCustomTag(action.request)
                    .pipe(
                        map(data => {
                            return CustomTagsActions.updateTagSuccess({
                                tag: data
                            })
                        }),
                        catchError(error => {
                            if (error.status === 500) {
                                return of(
                                    CustomTagsActions.backendFailure({ error })
                                );
                            }
                            return of(
                                CustomTagsActions.updateTagFailure({ error })
                            );
                        })
                    )
            })
        )
    );

    tagEntityOrUpdateTagFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    CustomTagsActions.tagEntityFailure,
                    CustomTagsActions.updateTagFailure
                ),
                tap((res) => {
                    this.toastrService.error(res.error.error);
                })
            ),
        { dispatch: false }
    );

    linkTagToEntityFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    CustomTagsActions.linkTagToEntityFailure
                ),
                tap((res) => {
                    this.toastrService.error(
                        this.translate.instant('portal.tags.already-tagged')
                    )
                })
            ),
        { dispatch: false }
    );

    unlinkTagFromEntityFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    CustomTagsActions.unlinkTagFromEntityFailure
                ),
                tap((res) => {
                    this.toastrService.error(
                        this.translate.instant('common.try-again-and-refresh')
                    )
                })
            ),
        { dispatch: false }
    );

    genericError$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    CustomTagsActions.backendFailure
                ),
                map(error => {
                    this.toastrService.error(
                        error.error.message ?? this.translate.instant('common.we-are-sorry-please-try-again'),
                        this.translate.instant('common.something_went_wrong')
                    );
                })
            ),
        { dispatch: false }
    );
}
